import { api } from './api'

export interface SignupRequest {
  email: string
}

export interface SignupResponse {
  id: number
  email: string
  name: string
  created_at: string
  updated_at: string
}

export interface SignupError {
  message: string
  errors?: Record<string, string[]>
}

export interface CompleteSignupRequest {
  full_name: string
  password: string
  password_confirmation: string
  reset_password_token: string
}

export interface CompleteSignupResponse {
  id: number
  email: string
  name: string
  updated_at: string
  token: string
}

export class SignupService {
  async newConfirmation(token: string): Promise<any> {
    try {
      const response = await api.post('/new_confirmation', {
        confirmation: { token: token }
      })
      
      return response.data
    } catch (error: any) {
      console.error('Confirmation error:', error)
      throw error
    }
  }

  async getStarted(email: string): Promise<SignupResponse> {
    try {
      const response = await api.post<SignupResponse>('/get_started', {
        user: {
          email: email.trim().toLowerCase()
        }
      })
      
      return response.data
    } catch (error: any) {
      // Log the full error for debugging
      console.error('Full signup error:', error)
      console.error('Error response:', error.response)
      console.error('Error response data:', error.response?.data)
      
      if (error.response?.status === 409) {
        // Email already exists
        throw {
          response: {
            status: 409,
            data: {
              message: 'Email already exists'
            }
          }
        }
      }
      
      if (error.response?.data?.errors) {
        // Validation errors
        throw {
          response: {
            status: error.response.status,
            data: error.response.data
          }
        }
      }
      
      // Generic error - include the actual error message from API
      const apiErrorMessage = error.response?.data?.detail || error.response?.data?.message || 'An error occurred during signup'
      throw {
        response: {
          status: error.response?.status || 500,
          data: {
            message: apiErrorMessage
          }
        }
      }
    }
  }

  /**
   * Complete signup: set the user's full name and password after invitation.
   * Uses the shared `api` client so the request resolves to the configured
   * gateway base URL (api.glaium.io/gateway) in production and the Vite proxy
   * in dev — unlike a relative fetch('/api/signup'), which in production hits
   * edge.glaium.io's nginx and returns a 405 HTML page.
   */
  async completeSignup(data: CompleteSignupRequest): Promise<CompleteSignupResponse> {
    try {
      const response = await api.put<CompleteSignupResponse>('/signup', {
        user: {
          full_name: data.full_name,
          password: data.password,
          password_confirmation: data.password_confirmation,
          reset_password_token: data.reset_password_token
        }
      })
      return response.data
    } catch (error: any) {
      console.error('Complete signup error:', error)
      // The api error handler returns { message, ... }; older/validation
      // paths may use detail.errors[]. Fall back gracefully.
      const apiErrorMessage =
        error.response?.data?.message ||
        error.response?.data?.details?.errors?.[0] ||
        error.response?.data?.detail?.errors?.[0] ||
        error.response?.data?.detail ||
        'Failed to set up password'
      throw new Error(
        typeof apiErrorMessage === 'string' ? apiErrorMessage : 'Failed to set up password'
      )
    }
  }
}

// Create singleton instance
const signupService = new SignupService()

export { signupService }

// Export factory function for compatibility with old_edge pattern
export const signUpService = () => signupService